import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Chip,
} from "@mui/material";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";

import PageHeader from "../../components/common/PageHeader";
import FormSection from "../../components/common/FormSection";
import LoadingScreen from "../../components/common/LoadingScreen";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";

import {
  useCreateTransferMutation,
  useGetTransferQuery,
  useUpdateTransferMutation,
} from "../../features/transfers/transfersApi";
import type { StopType, TransferStatus, TransferStop } from "../../features/transfers/types";

import {
  useListLocationsQuery,
  useListProvidersQuery,
  useListClientsQuery,
} from "../../features/masterdata/masterdataApi";
import AsyncAutocompleteWithQuickCreate from "../../components/common/AsyncAutocompleteWithQuickCreate";
import QuickCreateLocation from "../../components/quickCreate/QuickCreateLocation";
import QuickCreateClient from "../../components/quickCreate/QuickCreateClient";
import QuickCreateProvider from "../../components/quickCreate/QuickCreateProvider";
import type { Location, Client, Provider } from "../../features/masterdata/types";

type StopDraft = {
  sequence: number;
  stop_type: StopType;
  location: string;        // location id
  planned_time?: string;
  notes?: string;
};

function normalizeStops(stops: StopDraft[]) {
  return stops
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .map((s, i) => ({ ...s, sequence: i + 1 }));
}

export default function TransferFormPage() {
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();

  const isEdit = Boolean(id && id !== "new");
  const transferId = isEdit ? (id as string) : null;

  // masterdata for selects (MVP: first 400)
  const locationsQ = useListLocationsQuery({ page: 1, page_size: 400, ordering: "name" });
  const providersQ = useListProvidersQuery({ page: 1, page_size: 200, ordering: "name" });
  const clientsQ = useListClientsQuery({ page: 1, page_size: 200, ordering: "name" });

  const { data: existing, isLoading: isLoadingExisting } = useGetTransferQuery(transferId as any, {
    skip: !transferId,
  });

  const [createTransfer, { isLoading: isCreating }] = useCreateTransferMutation();
  const [updateTransfer, { isLoading: isUpdating }] = useUpdateTransferMutation();

  const saving = isCreating || isUpdating;

  // fields
  const [serviceDate, setServiceDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [status, setStatus] = useState<TransferStatus>("planned");
  const [description, setDescription] = useState("");

  const [client, setClient] = useState<string>("");
  const [provider, setProvider] = useState<string>("");

  const [priceToProvider, setPriceToProvider] = useState<string>("");
  const [priceToClient, setPriceToClient] = useState<string>("");

  const [stops, setStops] = useState<StopDraft[]>([
    { sequence: 1, stop_type: "pickup", location: "" },
    { sequence: 2, stop_type: "dropoff", location: "" },
  ]);

  // Quick Create: which modal is open; newly created entities to merge into options
  const [openQuickCreateClient, setOpenQuickCreateClient] = useState(false);
  const [openQuickCreateProvider, setOpenQuickCreateProvider] = useState(false);
  const [openQuickCreateLocation, setOpenQuickCreateLocation] = useState(false);
  /** When set, Location quick-create modal is open for this stop sequence */
  const [quickCreateLocationForSequence, setQuickCreateLocationForSequence] = useState<number | null>(null);
  const [newlyCreatedClient, setNewlyCreatedClient] = useState<Client | null>(null);
  const [newlyCreatedProvider, setNewlyCreatedProvider] = useState<Provider | null>(null);
  const [newlyCreatedLocation, setNewlyCreatedLocation] = useState<Location | null>(null);

  const clientsOptions = useMemo(
    () => [...(clientsQ.data?.results ?? []), ...(newlyCreatedClient ? [newlyCreatedClient] : [])],
    [clientsQ.data?.results, newlyCreatedClient]
  );
  const providersOptions = useMemo(
    () => [...(providersQ.data?.results ?? []), ...(newlyCreatedProvider ? [newlyCreatedProvider] : [])],
    [providersQ.data?.results, newlyCreatedProvider]
  );
  const locationsOptions = useMemo(
    () => [...(locationsQ.data?.results ?? []), ...(newlyCreatedLocation ? [newlyCreatedLocation] : [])],
    [locationsQ.data?.results, newlyCreatedLocation]
  );

  // Format price from API (number | null) for display: integer when whole, else 2 decimals
  const formatPriceForDisplay = (v: number | string | null | undefined): string => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (Number.isNaN(n)) return "";
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  // load existing in edit mode
  useEffect(() => {
    if (!existing) return;

    setServiceDate(existing.service_date || "");
    setStartTime(existing.start_time || "");
    setStatus(existing.status || "planned");
    setDescription(existing.description || "");

    setClient(existing.client || "");
    setProvider(existing.provider || "");

    setPriceToProvider(formatPriceForDisplay(existing.price_to_provider ?? null));
    setPriceToClient(formatPriceForDisplay(existing.price_to_client ?? null));

    const mapped: StopDraft[] = (existing.stops || []).map((s: TransferStop) => ({
      sequence: s.sequence,
      stop_type: s.stop_type,
      location: s.location_id ?? s.location ?? "",
      planned_time: s.planned_time || "",
      notes: s.notes || "",
    }));

    if (mapped.length >= 2) setStops(normalizeStops(mapped));
  }, [existing]);

  const canSave = useMemo(() => {
    if (!serviceDate) return false;
    if (stops.length < 2) return false;

    const hasPickup = stops.some((s) => s.stop_type === "pickup");
    const hasDrop = stops.some((s) => s.stop_type === "dropoff");
    if (!hasPickup || !hasDrop) return false;

    return true;
  }, [serviceDate, stops]);

  const addStop = () => {
    setStops((prev) =>
      normalizeStops([...prev, { sequence: prev.length + 1, stop_type: "stop", location: "" }])
    );
  };

  const removeStop = (seq: number) => {
    setStops((prev) => normalizeStops(prev.filter((s) => s.sequence !== seq)));
  };

  const moveStop = (seq: number, dir: "up" | "down") => {
    setStops((prev) => {
      const sorted = prev.slice().sort((a, b) => a.sequence - b.sequence);
      const idx = sorted.findIndex((s) => s.sequence === seq);
      const swapWith = dir === "up" ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= sorted.length) return prev;

      const a = sorted[idx];
      const b = sorted[swapWith];
      const tmp = a.sequence;
      a.sequence = b.sequence;
      b.sequence = tmp;

      return normalizeStops(sorted);
    });
  };

  const updateStop = (seq: number, patch: Partial<StopDraft>) => {
    setStops((prev) => normalizeStops(prev.map((s) => (s.sequence === seq ? { ...s, ...patch } : s))));
  };

  const onSave = async () => {
    try {
      const payload = {
        service_date: serviceDate,
        start_time: startTime || null,
        status,
        description: description || null,
        client: client || null,
        provider: provider || null,
        price_to_provider: priceToProvider ? Number(priceToProvider) : null,
        price_to_client: priceToClient ? Number(priceToClient) : null,
        stops: normalizeStops(stops).map((s) => ({
          sequence: s.sequence,
          stop_type: s.stop_type,
          location: s.location || null,
          planned_time: s.planned_time || null,
          notes: s.notes || null,
        })),
      };

      if (!transferId) {
        await createTransfer(payload as any).unwrap();
        enqueueSnackbar("Transfer created", { variant: "success" });
      } else {
        await updateTransfer({ id: transferId, patch: payload as any }).unwrap();
        enqueueSnackbar("Transfer updated", { variant: "success" });
      }

      nav("/app/transfers");
    } catch (e: any) {
      enqueueSnackbar(e?.data?.detail || "Failed to save transfer", { variant: "error" });
    }
  };

  if (isEdit && isLoadingExisting) {
    return <LoadingScreen label="Loading transfer…" />;
  }

  return (
    <Box>
      <PageHeader
        title={isEdit ? "Edit Transfer" : "New Transfer"}
        subtitle="Create a route with multi-stops"
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => nav("/app/transfers")} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" disabled={!canSave || saving} onClick={onSave}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Stack>
        }
      />

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Stack spacing={3}>
          <FormSection title="Route details" helperText="Date, time, client, provider, and pricing.">
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Service date"
                type="date"
                value={serviceDate}
                onChange={(e) => setServiceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              <TextField
                label="Start time (optional)"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Status"
                select
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
                fullWidth
              >
                <MenuItem value="planned">Planned</MenuItem>
                <MenuItem value="in_progress">In progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <AsyncAutocompleteWithQuickCreate<Client>
                label="Client"
                value={client}
                onChange={(id) => setClient(id ?? "")}
                options={clientsOptions}
                getOptionLabel={(c) => c.name}
                createLabel="Create new client"
                onOpenQuickCreate={() => setOpenQuickCreateClient(true)}
                loading={clientsQ.isLoading}
                placeholder="Search or create…"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <AsyncAutocompleteWithQuickCreate<Provider>
                label="Provider"
                value={provider}
                onChange={(id) => setProvider(id ?? "")}
                options={providersOptions}
                getOptionLabel={(p) => p.name}
                createLabel="Create new provider"
                onOpenQuickCreate={() => setOpenQuickCreateProvider(true)}
                loading={providersQ.isLoading}
                placeholder="Search or create…"
              />
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Price to provider (optional)"
              value={priceToProvider}
              onChange={(e) => setPriceToProvider(e.target.value)}
              placeholder="0.00"
              fullWidth
              inputProps={{ inputMode: "decimal", step: "0.01", min: 0 }}
            />
            <TextField
              label="Price to client (optional)"
              value={priceToClient}
              onChange={(e) => setPriceToClient(e.target.value)}
              placeholder="0.00"
              fullWidth
              inputProps={{ inputMode: "decimal", step: "0.01", min: 0 }}
            />
          </Stack>

            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Route or trip notes"
              fullWidth
            />
          </FormSection>

          <FormSection title="Stops" helperText="Define the route in order: at least one pickup and one dropoff. Location and planned time are optional. Use the arrows to reorder.">
            <Stack direction="row" justifyContent="flex-end" alignItems="center">
              <Button onClick={addStop} variant="outlined" startIcon={<ArrowDownwardOutlinedIcon sx={{ transform: "rotate(-90deg)" }} />}>
                Add stop
              </Button>
            </Stack>

            <Stack spacing={1.5} sx={{ position: "relative" }}>
              {stops.map((s, idx) => (
                <Paper
                  key={s.sequence}
                  variant="outlined"
                  sx={{
                    p: 2,
                    position: "relative",
                    borderLeft: "3px solid",
                    borderLeftColor:
                      s.stop_type === "pickup"
                        ? "success.main"
                        : s.stop_type === "dropoff"
                          ? "warning.main"
                          : "grey.400",
                  }}
                >
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 120 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: "action.selected",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          typography: "caption",
                          fontWeight: 700,
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Chip
                        size="small"
                        label={s.stop_type === "pickup" ? "Pickup" : s.stop_type === "dropoff" ? "Dropoff" : "Stop"}
                        color={s.stop_type === "pickup" ? "success" : s.stop_type === "dropoff" ? "warning" : "default"}
                        variant="filled"
                        onClick={() =>
                          updateStop(s.sequence, {
                            stop_type: s.stop_type === "pickup" ? "stop" : s.stop_type === "stop" ? "dropoff" : "pickup",
                          })
                        }
                        sx={{ textTransform: "capitalize", cursor: "pointer", minWidth: 88 }}
                        aria-label={`Stop type: ${s.stop_type}. Click to change.`}
                      />
                    </Stack>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <AsyncAutocompleteWithQuickCreate<Location>
                        label="Location (optional)"
                        value={s.location}
                        onChange={(id) => updateStop(s.sequence, { location: id ?? "" })}
                        options={locationsOptions}
                        getOptionLabel={(l) => l.name}
                        createLabel="Create new location"
                        onOpenQuickCreate={() => {
                          setQuickCreateLocationForSequence(s.sequence);
                          setOpenQuickCreateLocation(true);
                        }}
                        loading={locationsQ.isLoading}
                        placeholder="Search or create…"
                      />
                    </Box>

                    <TextField
                      label="Planned time (optional)"
                      type="time"
                      value={s.planned_time || ""}
                      onChange={(e) => updateStop(s.sequence, { planned_time: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ minWidth: 120 }}
                    />

                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" onClick={() => moveStop(s.sequence, "up")} disabled={idx === 0} aria-label="Move up">
                        <ArrowUpwardOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveStop(s.sequence, "down")} disabled={idx === stops.length - 1} aria-label="Move down">
                        <ArrowDownwardOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => removeStop(s.sequence)}
                        disabled={stops.length <= 2}
                        aria-label="Remove stop"
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>

                  <TextField
                    sx={{ mt: 2 }}
                    label="Notes (optional)"
                    value={s.notes || ""}
                    onChange={(e) => updateStop(s.sequence, { notes: e.target.value })}
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    placeholder="Stop-specific notes…"
                  />
                </Paper>
              ))}
            </Stack>
          </FormSection>
        </Stack>
      </Paper>

      <QuickCreateClient
        open={openQuickCreateClient}
        onClose={() => setOpenQuickCreateClient(false)}
        onSuccess={(c) => {
          setNewlyCreatedClient(c);
          setClient(c.id);
          setOpenQuickCreateClient(false);
        }}
      />
      <QuickCreateProvider
        open={openQuickCreateProvider}
        onClose={() => setOpenQuickCreateProvider(false)}
        onSuccess={(p) => {
          setNewlyCreatedProvider(p);
          setProvider(p.id);
          setOpenQuickCreateProvider(false);
        }}
      />
      <QuickCreateLocation
        open={openQuickCreateLocation}
        onClose={() => {
          setOpenQuickCreateLocation(false);
          setQuickCreateLocationForSequence(null);
        }}
        onSuccess={(loc) => {
          setNewlyCreatedLocation(loc);
          if (quickCreateLocationForSequence != null) {
            setStops((prev) =>
              normalizeStops(
                prev.map((stop) =>
                  stop.sequence === quickCreateLocationForSequence ? { ...stop, location: loc.id } : stop
                )
              )
            );
          }
          setOpenQuickCreateLocation(false);
          setQuickCreateLocationForSequence(null);
        }}
      />
    </Box>
  );
}
